/**
 * Pure consent logic for items 99 (email-click prompt) and 100 (US default).
 * Kept free of React and browser globals so every row of the decision table
 * can be unit-tested.
 *
 * Rules (binding):
 * - A stored choice always wins, whoever the visitor is.
 * - The US default applies to FUNCTIONAL only, only when the country is
 *   positively "US", and never when Global Privacy Control is on.
 * - Unknown / failed / pending country is NOT US: the opt-in path.
 * - Analytics is never defaulted on.
 */

export type ConsentSource = "user" | "us_default";

export interface StoredConsent {
  functional: boolean;
  analytics: boolean;
  source: ConsentSource;
}

/** "pending" = lookup not finished; null = unknown/failed; otherwise ISO code. */
export type GeoState = "pending" | string | null;

export type PromptKind = "none" | "wait" | "us_notice" | "email_prompt" | "banner";

/** Parse the localStorage record. Old records (no source) are treated as "user". */
export function parseStoredConsent(raw: string | null): StoredConsent | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<StoredConsent>;
    if (!p || typeof p !== "object") return null;
    return {
      functional: !!p.functional,
      analytics: !!p.analytics,
      source: p.source === "us_default" ? "us_default" : "user",
    };
  } catch {
    return null;
  }
}

export function isUsDefault(geo: GeoState, gpc: boolean): boolean {
  return geo === "US" && !gpc;
}

/** Effective preferences in force right now. */
export function effectivePrefs(
  stored: StoredConsent | null,
  geo: GeoState,
  gpc: boolean,
): { functional: boolean; analytics: boolean } {
  if (stored) return { functional: stored.functional, analytics: stored.analytics };
  return { functional: isUsDefault(geo, gpc), analytics: false };
}

/** Which prompt (if any) to show. */
export function decidePrompt(input: {
  stored: StoredConsent | null;
  geo: GeoState;
  gpc: boolean;
  hasPendingToken: boolean;
}): PromptKind {
  const { stored, geo, gpc, hasPendingToken } = input;
  if (stored) return "none";
  if (geo === "pending") return "wait";
  if (isUsDefault(geo, gpc)) return "us_notice";
  if (hasPendingToken) return "email_prompt";
  return "banner";
}
